export type CalendarSpotsFilter = 'all' | 'open' | 'full';

export interface ClassCalendarFilterInput {
  query: string;
  locationId: string;
  sessionId: string;
  day: string;
  age: string;
  spots: CalendarSpotsFilter;
}

export interface ClassCalendarFilterEvent {
  title: string;
  locationId?: string | null;
  sessionId?: string | null;
  day?: string;
  minAge?: number | null;
  maxAge?: number | null;
  spotsRemaining?: number | null;
}

export interface ClassCalendarTrialFilterEvent {
  resourceId: string;
  start: string;
}

function selectedDays(day: string): Array<string> {
  if (!day || day === 'All') return [];
  return day
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

function selectedValues(value: string): Array<string> {
  if (!value || value === 'All') return [];
  return value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

function ageMatches(
  age: string,
  minAge?: number | null,
  maxAge?: number | null,
): boolean {
  if (!age || age === 'All') return true;
  const wanted = Number(age);
  if (Number.isNaN(wanted)) return true;
  if (minAge == null || maxAge == null) return false;
  return wanted >= minAge && wanted <= maxAge;
}

function spotsMatch(
  spots: CalendarSpotsFilter,
  spotsRemaining?: number | null,
): boolean {
  if (spots === 'all') return true;
  if (spotsRemaining == null) return false;
  if (spots === 'open') return spotsRemaining > 0;
  return spotsRemaining === 0;
}

export function filterClassCalendarEvents<T extends ClassCalendarFilterEvent>(
  events: Array<T>,
  filter: ClassCalendarFilterInput,
): Array<T> {
  const query = filter.query.trim().toLowerCase();
  const days = selectedDays(filter.day);
  const locationIds = selectedValues(filter.locationId);
  const sessionIds = selectedValues(filter.sessionId);

  return events.filter((event) => {
    if (query && !event.title.toLowerCase().includes(query)) return false;
    if (
      locationIds.length > 0 &&
      !locationIds.includes(event.locationId ?? '')
    ) {
      return false;
    }
    if (sessionIds.length > 0 && !sessionIds.includes(event.sessionId ?? '')) {
      return false;
    }
    if (days.length > 0 && !days.includes(event.day ?? '')) return false;
    if (!ageMatches(filter.age, event.minAge, event.maxAge)) return false;
    return spotsMatch(filter.spots, event.spotsRemaining);
  });
}

export function filterEventsWithBookedTrials<
  T extends ClassCalendarTrialFilterEvent,
>(
  events: Array<T>,
  bookedTrialsByOccurrence: Record<string, number> | undefined,
  trialsOnly: boolean,
): Array<T> {
  if (!trialsOnly) return events;

  return events.filter((event) => {
    const occurrenceDay = event.start.slice(0, 10);
    return (
      (bookedTrialsByOccurrence?.[`${event.resourceId}|${occurrenceDay}`] ??
        0) > 0
    );
  });
}
